import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { authorizedAxios } from "@moonscape/shared";

export interface AssetJourneyTimelineEntry {
  id: number;
  type: string;
  timestamp: number;
  date: string;
  exchange: string;
  received: number;
  spent: number;
  balance: number;
  ext_txn_id?: string;
}

export interface InternalTransfer {
  send_id: number;
  send_ext_txn_id?: string;
  receive_id: number;
  receive_ext_txn_id?: string;
  amount: number;
  from_exchange: string;
  to_exchange: string;
  send_timestamp: number;
  receive_timestamp: number;
  is_internal: boolean;
}

export interface JourneyNarrativeEntry {
  timestamp: number;
  date: string;
  type: string;
  text: string;
  ids: number[];
}

export interface AssetJourneySummary {
  total_received: number;
  total_spent: number;
  total_bought: number;
  total_sold: number;
  total_income: number;
  total_external_receives: number;
  total_external_sends: number;
  total_internal_transferred: number;
  final_balance: number;
  num_transactions: number;
  num_internal_transfers: number;
}

export interface AssetJourneyData {
  asset: string;
  asset_symbol: string;
  timeline: AssetJourneyTimelineEntry[];
  matched_transfers: InternalTransfer[];
  narrative: JourneyNarrativeEntry[];
  summary: AssetJourneySummary;
}

/**
 * Fetches cross-wallet journey for an asset.
 * Shows timeline, matched internal transfers, and narrative.
 */
export const useAssetJourney = (asset: string | null, enabled: boolean = true) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const email = user?.email;

  const getAssetJourney = async (): Promise<AssetJourneyData | null> => {
    if (!asset) return null;

    const token = await getAccessTokenSilently();
    const url = `/audit/asset-journey/${encodeURIComponent(asset)}`;

    console.log("[useAssetJourney] fetching", url);
    const res = await authorizedAxios(token).get(url);

    if (res.data?.success) {
      return res.data.data as AssetJourneyData;
    }
    throw new Error(res.data?.error || "Failed to fetch asset journey");
  };

  const {
    isLoading,
    isError,
    error,
    data: journeyData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["asset-journey", asset],
    queryFn: getAssetJourney,
    enabled: !!email && !!asset && enabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return { isLoading, isError, error, journeyData, isFetching, refetch };
};
